import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, Navigation } from "lucide-react";
import { loadMaps } from "@/lib/geo";

// Google Places Autocomplete with a SESSION TOKEN so predictions + the final getDetails
// are billed as ONE session, not per keystroke. Maps JS is lazy-loaded on first focus.
export const PlaceAutocomplete = ({ value = "", onChange, placeholder = "Search a place…", testid = "place" }) => {
  const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [text, setText] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [preds, setPreds] = useState([]);
  const [loading, setLoading] = useState(false);
  const acRef = useRef(null);   // AutocompleteService
  const psRef = useRef(null);   // PlacesService
  const tokRef = useRef(null);  // session token
  const readyRef = useRef(false);
  const tmr = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => { setText(value || ""); }, [value]);
  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const ensure = async () => {
    if (readyRef.current || !key) return;
    await loadMaps(key);
    const g = window.google;
    acRef.current = new g.maps.places.AutocompleteService();
    psRef.current = new g.maps.places.PlacesService(document.createElement("div"));
    tokRef.current = new g.maps.places.AutocompleteSessionToken();
    readyRef.current = true;
  };

  const onFocus = async () => { setOpen(true); await ensure(); };

  const onType = (v) => {
    setText(v); setOpen(true);
    onChange && onChange(v, {});
    if (tmr.current) clearTimeout(tmr.current);
    if (!v.trim() || !acRef.current) { setPreds([]); return; }
    tmr.current = setTimeout(() => {
      setLoading(true);
      acRef.current.getPlacePredictions(
        { input: v, sessionToken: tokRef.current, componentRestrictions: { country: "in" } },
        (res) => { setPreds(res || []); setLoading(false); }
      );
    }, 300);
  };

  const pick = (p) => {
    setText(p.description); setOpen(false); setPreds([]);
    if (!psRef.current) { onChange && onChange(p.description, {}); return; }
    psRef.current.getDetails(
      { placeId: p.place_id, sessionToken: tokRef.current, fields: ["formatted_address", "geometry", "name"] },
      (det) => {
        const g = window.google;
        const loc = det?.geometry?.location;
        const addr = det?.formatted_address || p.description;
        onChange && onChange(addr, loc ? { lat: loc.lat(), lng: loc.lng(), place_id: p.place_id } : {});
        // getDetails closes the billing session — start a fresh token for the next search.
        tokRef.current = g?.maps?.places ? new g.maps.places.AutocompleteSessionToken() : null;
      }
    );
  };

  if (!key) {
    return <input value={text} onChange={(e) => { setText(e.target.value); onChange && onChange(e.target.value, {}); }} placeholder={placeholder} data-testid={testid} className="mt-1 h-9 w-full rounded-md border border-slate-200 px-2 text-sm" />;
  }
  return (
    <div ref={boxRef} className="relative" data-testid={`${testid}-wrap`}>
      <div className="mt-1 flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2">
        <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" />
        <input value={text} onFocus={onFocus} onChange={(e) => onType(e.target.value)} placeholder={placeholder} data-testid={testid} className="h-9 min-w-0 flex-1 bg-transparent text-sm outline-none" />
      </div>
      {open && (text.trim() || loading) && (
        <div className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-slate-200 bg-white shadow-lg" data-testid={`${testid}-dropdown`}>
          {loading && <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…</div>}
          {!loading && preds.map((p) => (
            <button key={p.place_id} type="button" onClick={() => pick(p)} data-testid={`${testid}-opt-${p.place_id}`} className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-sky-50">
              <Navigation className="h-3.5 w-3.5 shrink-0 text-sky-500" />
              <span className="min-w-0 text-sm text-slate-700">{p.description}</span>
            </button>
          ))}
          {!loading && text.trim() && preds.length === 0 && <div className="px-3 py-2 text-xs text-slate-400">No matches — keep typing.</div>}
        </div>
      )}
    </div>
  );
};

export default PlaceAutocomplete;
